import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import _ from 'lodash';

import CodingRepository from '../repositories/codingRepository';
import { queryClient } from '../router/queryClient';
import type { CodingEntryTeam, CodingListElement } from '../zustandStore/slices';

import { companyKeys } from './companyQuery';
import { codingKeys } from './query-keys';

export type CodingList = {
	ID: string;
	code: string;
	name: string;
	group: string;
	ordering: string;
	description: string;
	targets: string[];
	enabled: boolean;
};

export const getCodingListsQuery = ({ skipDisabled }: { skipDisabled: boolean }) =>
	queryOptions({
		queryKey: [codingKeys.getCodingLists, skipDisabled],
		queryFn: () => CodingRepository.getCodingLists({ skipDisabled }),
	});

export const useUpdateCodingListQuery = () => {
	return useMutation({
		mutationFn: CodingRepository.saveCodingList,
		onSuccess: () => {
			return queryClient.invalidateQueries({
				queryKey: [codingKeys.getCodingLists],
			});
		},
	});
};

export const useDeleteCodingListQuery = () => {
	return useMutation({
		mutationFn: CodingRepository.deleteCodingList,
		onSuccess: () => {
			return queryClient.invalidateQueries({
				queryKey: [codingKeys.getCodingLists],
			});
		},
	});
};

export const getCodingListsEntriesQuery = ({ codingLists }: { codingLists: CodingList[] | undefined }) => {
	return queryOptions({
		queryKey: [codingKeys.getCodingListsEntries, codingLists],
		queryFn: () => {
			return Array.isArray(codingLists)
				? Promise.all(
						codingLists.map(async codingList => {
							const codingListEntries = await CodingRepository.getCodingListEntries({ codingListCode: codingList?.ID });

							return {
								code: codingList?.ID,
								values: codingListEntries,
							};
						}),
					)
				: [];
		},
		enabled: Array.isArray(codingLists),
	});
};

export const useGetInactiveCodingListEntriesQuery = company =>
	useQuery({
		queryKey: [codingKeys.getInactiveCodingListEntries, company.companyId],
		queryFn: () => CodingRepository.getInactiveCodingListEntries({ companyId: company.companyId }),
		enabled: !!_.get(company, 'companyId'),
	});

export const useDeleteCodingListEntryQuery = () => {
	return useMutation({
		mutationFn: CodingRepository.deleteCodingListEntry,
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: [codingKeys.getCodingLists],
			});
			void queryClient.invalidateQueries({
				queryKey: [codingKeys.getCodingListsEntries],
			});
			void queryClient.invalidateQueries({
				queryKey: [codingKeys.getInactiveCodingListEntries],
			});
		},
	});
};

export const useSaveCodingListEntryQuery = () => {
	return useMutation({
		mutationFn: ({
			codingListCode,
			codingEntryId,
			codingEntryLabel,
		}: {
			codingListCode: CodingListElement['id'];
			codingEntryId: string;
			codingEntryLabel: string;
		}) => CodingRepository.saveCodingListEntry({ codingListCode, codingEntryId, codingEntryLabel }),
		onSuccess: async () => {
			void queryClient.invalidateQueries({
				queryKey: [codingKeys.getCodingLists],
			});
			void queryClient.invalidateQueries({
				queryKey: [codingKeys.getCodingListsEntries],
			});
			void queryClient.invalidateQueries({
				queryKey: [codingKeys.getInactiveCodingListEntries],
			});
		},
	});
};

export const useChangeCodingListEntryIdQuery = () => {
	return useMutation({
		mutationFn: ({
			codingListCode,
			codingEntryId,
			newCodingEntryId,
			codingEntryLabel,
		}: {
			codingListCode: CodingListElement['id'];
			codingEntryId: string;
			newCodingEntryId: string;
			codingEntryLabel: string;
		}) =>
			CodingRepository.changeCodingListEntryId({
				codingListCode,
				codingEntryId,
				newCodingEntryId,
				codingEntryLabel,
			}),
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [codingKeys.getCodingLists],
			});
			void queryClient.invalidateQueries({
				queryKey: [codingKeys.getCodingListsEntries],
			});
			void queryClient.invalidateQueries({
				queryKey: [codingKeys.getInactiveCodingListEntries],
			});
		},
	});
};

export const useToggleCodingListEntryQuery = () => {
	return useMutation<
		AxiosResponse,
		AxiosError,
		{
			codingListCode: CodingListElement['id'];
			codingEntryId: string;
			teamId: CodingEntryTeam['teamId'];
			state: CodingEntryTeam['state'];
		}
	>({
		mutationFn: ({ codingListCode, codingEntryId, teamId, state }) =>
			CodingRepository.toggleCodingListEntry({ codingListCode, codingEntryId, teamId, state }),
		onSuccess: () => {
			return queryClient.invalidateQueries({
				queryKey: [codingKeys.getInactiveCodingListEntries],
			});
		},
	});
};

type ProviderMappingResponse = 'AMEX' | 'none' | '';

export const useGetProviderMappingQuery = () =>
	useQuery<ProviderMappingResponse, Error>({
		queryKey: [codingKeys.getProviderMapping],
		queryFn: () => CodingRepository.getProviderMapping(),
	});

export const useSaveProviderMappingQuery = () => {
	return useMutation<AxiosResponse, AxiosError, 'AMEX'>({
		mutationFn: CodingRepository.setProviderMapping,
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: [codingKeys.getProviderMapping],
			});
			void queryClient.invalidateQueries({
				queryKey: companyKeys.getFeatures(),
			});
		},
	});
};

export const useDeleteProviderMappingQuery = () => {
	return useMutation<AxiosResponse, AxiosError>({
		mutationFn: CodingRepository.deleteProviderMapping,
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: [codingKeys.getProviderMapping],
			});
			void queryClient.invalidateQueries({
				queryKey: companyKeys.getFeatures(),
			});
		},
	});
};

export const useGetProviderFieldsQuery = () =>
	useQuery({ queryKey: [codingKeys.getProviderFields], queryFn: () => CodingRepository.getProviderFields() });

export const useSaveProviderFieldsQuery = () => {
	return useMutation<AxiosResponse, AxiosError, { providerFields: typeof CodingRepository.DEFAULT_PROVIDER_MAPPING }>({
		mutationFn: CodingRepository.saveProviderFields,
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: [codingKeys.getProviderFields],
			});
		},
	});
};

export const useSetCodingEntryState = () => {
	// TODO: this looks unused do we need it ?
	return useMutation({
		mutationFn: CodingRepository.setCodingEntryState,
		onSuccess: () => {
			return queryClient.invalidateQueries({
				queryKey: [codingKeys.getInactiveCodingListEntries],
			});
		},
	});
};

export const useDisableCodingListQuery = () => {
	return useMutation({
		mutationFn: CodingRepository.disableCodingList,
		onSettled: () => {
			void queryClient.invalidateQueries({ queryKey: [codingKeys.getCodingLists, false] });
		},
	});
};

export const useEnableCodingListQuery = () => {
	return useMutation({
		mutationFn: CodingRepository.enableCodingList,
		onSettled: () => {
			void queryClient.invalidateQueries({ queryKey: [codingKeys.getCodingLists, false] });
		},
	});
};
